
import { isStreamDebug, streamLog, textInfo } from "./debug";
import {
  CardState,
  Direction,
  NormalizedEvent,
  Party,
  SegmentState,
  SegmentStatus,
  StreamState,
  createInitialState,
  isEnglishLang,
  isForeignLang,
  langBucket
} from "./types";

const joinChunks = (chunks: Map<number, string>): string => {
  const keys = Array.from(chunks.keys()).sort((a, b) => a - b);
  if (keys.length === 0) return "";
  if (keys[0] !== 0) return "";
  let out = "";
  let expected = 0;
  for (const k of keys) {
    if (k !== expected) break;
    out += chunks.get(k) || "";
    expected++;
  }
  return out;
};

const defaultDirection = (party: Party): Direction =>
  party === "agent" ? "outbound" : "inbound";

function cloneState(state: StreamState): StreamState {
  return {
    ...state,
    cardOrder: state.cardOrder.slice(),
    cards: { ...state.cards },
    segments: { ...state.segments },
    bindKeyToSeg: { ...state.bindKeyToSeg }
  };
}

function touchSeg(state: StreamState, segId: string): SegmentState {
  const cur = state.segments[segId];
  const next: SegmentState = { ...cur, chunks: cur.chunks };
  state.segments[segId] = next;
  return next;
}

function touchCard(state: StreamState, cardId: string): CardState {
  const cur = state.cards[cardId];
  const next: CardState = { ...cur, segIds: cur.segIds.slice() };
  state.cards[cardId] = next;
  return next;
}


function assignCard(state: StreamState, seg: SegmentState): CardState {
  const segLang = langBucket(seg.sourceLang);

  const lastId = state.cardOrder.length
    ? state.cardOrder[state.cardOrder.length - 1]
    : null;
  const lastCard = lastId ? state.cards[lastId] : null;
  if (
    lastCard &&
    lastCard.party === seg.party &&
    langBucket(lastCard.sourceLang) === segLang
  ) {
    const card = touchCard(state, lastCard.id);
    card.segIds.push(seg.segId);
    if (!card.sourceLang && seg.sourceLang) card.sourceLang = seg.sourceLang;
    if (seg.aiInterpreter) card.aiInterpreter = true;
    if (card.createdAt == null && seg.createdAt != null) card.createdAt = seg.createdAt;
    state.lastCardId = card.id;
    return card;
  }

  const id = `c${++state.cardCounter}`;
  const card: CardState = {
    id,
    party: seg.party,
    direction: seg.direction,
    sourceLang: seg.sourceLang,
    orderSeq: seg.arrival,
    createdAt: seg.createdAt,
    aiInterpreter: !!seg.aiInterpreter,
    historical: seg.historical,
    segIds: [seg.segId]
  };
  state.cards[id] = card;
  state.cardOrder.push(id);
  state.lastCardId = id;
  return card;
}

function createSegment(
  state: StreamState,
  party: Party,
  e: Partial<NormalizedEvent>,
  opts: { orderSeq?: number; historical?: boolean } = {}
): SegmentState {
  const segId = `s${++state.segCounter}`;
  const arrival = ++state.arrivalCounter;
  const orderSeq =
    opts.orderSeq != null
      ? opts.orderSeq
      : typeof e.orderSeq === "number"
      ? e.orderSeq
      : 1e9 + arrival;

  const seg: SegmentState = {
    segId,
    cardId: "",
    party,
    direction: e.direction || defaultDirection(party),
    arrival,
    orderSeq,
    createdAt: e.createdAt,
    sourceLang: e.sourceLang || "",
    targetLang: e.targetLang || "",
    rolling: "",
    interp: "",
    englishSource: "none",
    chunks: new Map<number, string>(),
    interpStarted: false,
    interpFinal: false,
    rollingRevision: 0,
    hasRolling: false,
    rollingFinal: false,
    status: "streaming-transcription",
    finalized: false,
    hasRealId: !!(e.segmentId || e.turnId || (e.rawId && !e.isLive)),
    source: e.source,
    aiInterpreter: e.aiInterpreter,
    historical: opts.historical ?? e.historical ?? false
  };
  state.segments[segId] = seg;
  const card = assignCard(state, seg);
  seg.cardId = card.id;

  for (const id of Object.keys(state.segments)) {
    const other = state.segments[id];
    if (
      other.party === party &&
      other.segId !== segId &&
      other.hasRolling &&
      !other.rollingFinal
    ) {
      const t = touchSeg(state, other.segId);
      t.rollingFinal = true;
      if (isStreamDebug()) {
        streamLog("reduce", "orphan-finalize superseded open segment", {
          party,
          orphanSeg: other.segId,
          orphanCard: other.cardId,
          newSeg: segId,
          newCard: card.id
        });
      }
    }
  }

  return seg;
}

function bindKeys(e: NormalizedEvent): string[] {
  const keys: string[] = [];
  if (e.segmentId) keys.push(`seg:${e.party}:${e.segmentId}`);
  else if (e.turnId) keys.push(`turn:${e.party}:${e.turnId}`);
  else if (e.isLive) keys.push(`live:${e.party}`);
  if (e.kind === "transcript" && e.rawId && !e.isLive) {
    const uid = e.rawId.replace(/^(inbound|outbound):/, "");
    keys.push(`uid:${e.party}:${uid}`);
  }
  return keys;
}

function registerKeys(state: StreamState, keys: string[], segId: string): void {
  for (const k of keys) state.bindKeyToSeg[k] = segId;
}

function bindKeysMatchExisting(state: StreamState, e: NormalizedEvent): boolean {
  return bindKeys(e).some((k) => !!state.bindKeyToSeg[k]);
}

function isPartyTip(state: StreamState, seg: SegmentState): boolean {
  for (const id in state.segments) {
    const s = state.segments[id];
    if (s.party === seg.party && s.arrival > seg.arrival) return false;
  }
  return true;
}

function partyStreamingTip(state: StreamState, party: Party): SegmentState | null {
  let best: SegmentState | null = null;
  for (const id in state.segments) {
    const s = state.segments[id];
    if (s.party !== party) continue;
    if (!s.hasRealId) continue;
    if (s.finalized || s.rollingFinal || !s.hasRolling) continue;
    if (!best || s.arrival > best.arrival) best = s;
  }
  return best;
}

function partyOpenForeignTip(state: StreamState, party: Party): SegmentState | null {
  let best: SegmentState | null = null;
  for (const id in state.segments) {
    const s = state.segments[id];
    if (s.party !== party) continue;
    if (s.finalized || !s.hasRolling) continue;
    if (!isForeignLang(s.sourceLang)) continue;
    if (s.englishSource === "delta") continue;
    if (!best || s.arrival > best.arrival) best = s;
  }
  return best;
}

function resolveSegment(state: StreamState, e: NormalizedEvent): SegmentState {
  const keys = bindKeys(e);
  for (const k of keys) {
    const existing = state.bindKeyToSeg[k];
    if (existing && state.segments[existing]) {
      const seg = state.segments[existing];
      if (seg.party !== e.party) continue;
      if (k.startsWith("live:") && (seg.finalized || !isPartyTip(state, seg))) continue;
      registerKeys(state, keys, existing);
      return seg;
    }
  }

  const isIdlessLivePreview = keys.some((k) => k.startsWith("live:"));
  if (
    isIdlessLivePreview &&
    !!e.english &&
    !!e.english.trim() &&
    isForeignLang(e.sourceLang)
  ) {
    const tip = partyOpenForeignTip(state, e.party);
    if (tip) {
      registerKeys(state, keys, tip.segId);
      return tip;
    }
  }

  if (isIdlessLivePreview) {
    const streaming = partyStreamingTip(state, e.party);
    if (streaming) {
      registerKeys(state, keys, streaming.segId);
      return streaming;
    }
  }

  const looksTypeToTalk =
    e.party === "agent" && (e.source === "type-to-talk" || e.kind === "interpretation");
  if (
    looksTypeToTalk &&
    state.pendingTypeToTalkSeg &&
    state.segments[state.pendingTypeToTalkSeg] &&
    !state.segments[state.pendingTypeToTalkSeg].finalized
  ) {
    const seg = state.segments[state.pendingTypeToTalkSeg];
    state.pendingTypeToTalkSeg = null;
    registerKeys(state, keys, seg.segId);
    return seg;
  }

  const seg = createSegment(state, e.party, e);
  registerKeys(state, keys, seg.segId);
  return seg;
}

function textLen(s: string): number {
  return (s || "").trim().length;
}

function incomingTranscriptText(e: NormalizedEvent): string {
  return (e.original || e.english || "").trim();
}

function segmentCreatedMs(seg: SegmentState): number {
  if (seg.createdAt == null) return 0;
  const ms = typeof seg.createdAt === "number" ? seg.createdAt : Date.parse(String(seg.createdAt));
  return Number.isFinite(ms) ? ms : 0;
}

function eventCreatedMs(e: NormalizedEvent): number {
  if (e.createdAt == null) return 0;
  const ms = typeof e.createdAt === "number" ? e.createdAt : Date.parse(String(e.createdAt));
  return Number.isFinite(ms) ? ms : 0;
}

function isStaleLivePreview(state: StreamState, e: NormalizedEvent): boolean {
  if (!e.isLive || e.isFinal) return false;
  const incoming = incomingTranscriptText(e);
  if (!incoming) return false;
  const incomingMs = eventCreatedMs(e);
  for (const id of Object.keys(state.segments)) {
    const s = state.segments[id];
    if (s.party !== e.party) continue;
    const have = (s.rolling || s.interp || "").trim();
    if (!have) continue;
    if (have.startsWith(incoming) && have.length > incoming.length) return true;
    if (
      !s.rollingFinal &&
      !s.finalized &&
      !s.historical &&
      incomingMs > 0 &&
      textLen(have) > textLen(incoming) &&
      segmentCreatedMs(s) > incomingMs
    ) {
      return true;
    }
  }
  return false;
}

function applyTranscript(state: StreamState, e: NormalizedEvent): void {
  if (e.isTranslationUpdate) {
    const keys = bindKeys(e);
    let segId: string | undefined;
    for (const k of keys) if (state.bindKeyToSeg[k]) segId = state.bindKeyToSeg[k];
    if (!segId || !state.segments[segId]) return;
    const seg = touchSeg(state, segId);
    if (e.english && seg.englishSource !== "delta") {
      seg.interp = e.english;
      seg.englishSource = "mt";
    }
    if (e.targetLang) seg.targetLang = e.targetLang;
    return;
  }

  const hasContent = !!(e.original && e.original.trim());
  if (!hasContent && !bindKeysMatchExisting(state, e)) return;

  if (isStaleLivePreview(state, e) && !bindKeysMatchExisting(state, e)) {
    if (isStreamDebug()) {
      streamLog("reduce", "drop stale live preview (would open a new card)", {
        party: e.party,
        text: textInfo(e.original || e.english)
      });
    }
    return;
  }

  const target = resolveSegment(state, e);

  if (e.revision != null && target.hasRolling && e.revision < target.rollingRevision) return;
  if (target.rollingFinal && !e.isFinal) return;

  const incoming = incomingTranscriptText(e);
  const current = (target.rolling || "").trim();
  if (
    e.isLive &&
    !e.isFinal &&
    current &&
    incoming &&
    incoming.length < current.length &&
    current.startsWith(incoming)
  ) {
    if (isStreamDebug()) {
      streamLog("reduce", "drop stale live preview (would shrink rolling)", {
        party: e.party,
        segId: target.segId
      });
    }
    return;
  }

  const seg = touchSeg(state, target.segId);
  if (e.original != null && e.original !== "") seg.rolling = e.original;
  if (e.sourceLang) seg.sourceLang = e.sourceLang;
  if (e.targetLang) seg.targetLang = e.targetLang;
  if (e.direction) seg.direction = e.direction;
  if (e.source) seg.source = e.source;
  if (e.aiInterpreter) seg.aiInterpreter = true;
  if (e.createdAt != null && seg.createdAt == null) seg.createdAt = e.createdAt;
  if (typeof e.orderSeq === "number") seg.orderSeq = e.orderSeq;
  if (e.revision != null) seg.rollingRevision = Math.max(seg.rollingRevision, e.revision);
  seg.hasRolling = true;

  if (seg.englishSource !== "delta") {
    if (e.english && e.english.trim()) {
      seg.interp = e.english;
      seg.englishSource = "mt";
    } else if (isEnglishLang(seg.sourceLang) && seg.rolling) {
      seg.interp = seg.rolling;
      seg.englishSource = "mt";
    }
  }

  if (e.isFinal) seg.rollingFinal = true;

  propagateSegLang(state, seg);
}

function applyInterpretation(state: StreamState, e: NormalizedEvent): void {
  if (!e.segmentId && !e.turnId && !bindKeysMatchExisting(state, e)) return;

  const target = resolveSegment(state, e);

  if (target.finalized && (e.done || e.utteranceEnd) && !e.chunk) return;

  const seq = typeof e.seq === "number" ? e.seq : target.chunks.size;
  const chunk = e.chunk || "";
  const existing = target.chunks.get(seq);
  if (existing !== undefined && existing === chunk) {
    if (e.done || e.utteranceEnd) {
      const s = touchSeg(state, target.segId);
      s.interpStarted = true;
      s.interpFinal = true;
    }
    return;
  }

  const seg = touchSeg(state, target.segId);
  seg.chunks = new Map(seg.chunks);
  seg.chunks.set(seq, chunk);
  seg.interpStarted = true;
  seg.interp = joinChunks(seg.chunks);
  seg.englishSource = "delta";
  if (typeof e.orderSeq === "number") seg.orderSeq = e.orderSeq;
  if (e.direction && seg.direction === defaultDirection(seg.party)) seg.direction = e.direction;
  if (e.createdAt != null && seg.createdAt == null) seg.createdAt = e.createdAt;
  if (e.done || e.utteranceEnd) seg.interpFinal = true;
}

function applyTypeToTalk(state: StreamState, e: NormalizedEvent): void {
  const seg = createSegment(state, "agent", e);
  const t = touchSeg(state, seg.segId);
  t.rolling = e.original || "";
  t.sourceLang = e.sourceLang || "en";
  t.targetLang = e.targetLang || "en";
  t.hasRolling = true;
  t.rollingFinal = false; // stay open so the backend echo can reconcile
  if (e.english) {
    t.interp = e.english;
    t.englishSource = "mt";
  } else if (isEnglishLang(t.sourceLang)) {
    t.interp = t.rolling;
    t.englishSource = "mt";
  }
  state.pendingTypeToTalkSeg = seg.segId;
  propagateSegLang(state, t);
}

function propagateSegLang(state: StreamState, seg: SegmentState): void {
  const card = state.cards[seg.cardId];
  if (card && !card.sourceLang && seg.sourceLang) {
    const c = touchCard(state, card.id);
    c.sourceLang = seg.sourceLang;
  }
}

function computeFinalized(seg: SegmentState): boolean {
  const hasContent = seg.hasRolling || seg.interpStarted;
  if (!hasContent) return false;
  const rollingSettled = !seg.hasRolling || seg.rollingFinal;
  const expectsInterp = seg.hasRolling && isForeignLang(seg.sourceLang);
  const interpSettled = seg.interpStarted ? seg.interpFinal : !expectsInterp;
  return rollingSettled && interpSettled;
}

function computeStatus(seg: SegmentState): SegmentStatus {
  if (seg.finalized) return "finalized";
  if (seg.interpStarted && !seg.interpFinal) return "streaming-interpretation";
  if (
    seg.hasRolling &&
    seg.rollingFinal &&
    isForeignLang(seg.sourceLang) &&
    !seg.interpStarted
  ) {
    return "awaiting-interpretation";
  }
  return "streaming-transcription";
}

function recompute(state: StreamState): void {
  for (const segId of Object.keys(state.segments)) {
    const seg = state.segments[segId];
    const finalized = seg.finalized || computeFinalized(seg);
    const status = finalized
      ? "finalized"
      : computeStatus({ ...seg, finalized } as SegmentState);
    if (finalized !== seg.finalized || status !== seg.status) {
      const s = touchSeg(state, segId);
      s.finalized = finalized;
      s.status = status;
    }
  }

  for (const cardId of state.cardOrder) {
    const card = state.cards[cardId];
    const sorted = card.segIds
      .slice()
      .sort((a, b) => state.segments[a].arrival - state.segments[b].arrival);
    if (sorted.join("|") !== card.segIds.join("|")) {
      const c = touchCard(state, cardId);
      c.segIds = sorted;
    }
  }

  let activeArrival = -Infinity;
  let activeSegId: string | null = null;
  for (const segId of Object.keys(state.segments)) {
    const seg = state.segments[segId];
    if (seg.finalized) continue;
    if (seg.arrival > activeArrival) {
      activeArrival = seg.arrival;
      activeSegId = segId;
    }
  }
  state.activeSegId = activeSegId;
}

export function reduce(state: StreamState, event: NormalizedEvent): StreamState {
  const next = cloneState(state);
  const segBefore = next.segCounter;
  const cardBefore = next.cardCounter;

  if (event.kind === "interpretation") {
    applyInterpretation(next, event);
  } else if (event.source === "type-to-talk" && event.optimistic) {
    applyTypeToTalk(next, event);
  } else {
    applyTranscript(next, event);
  }
  recompute(next);

  if (isStreamDebug()) {
    const boundKey = event.segmentId
      ? `seg:${event.party}:${event.segmentId}`
      : event.turnId
      ? `turn:${event.party}:${event.turnId}`
      : event.isLive
      ? `live:${event.party}`
      : "";
    const boundSeg = boundKey ? next.bindKeyToSeg[boundKey] : undefined;
    const boundCard = boundSeg ? next.segments[boundSeg]?.cardId : undefined;
    streamLog("reduce", event.kind, {
      party: event.party,
      segmentId: event.segmentId,
      rawId: event.rawId,
      isLive: event.isLive,
      orderSeq: event.orderSeq,
      isFinal: event.isFinal,
      done: event.done || event.utteranceEnd,
      seq: event.seq,
      isTranslationUpdate: event.isTranslationUpdate,
      text: textInfo(event.kind === "interpretation" ? event.chunk : event.original || event.english),
      boundSeg: boundSeg || "?",
      boundCard: boundCard || "?",
      newSeg: next.segCounter > segBefore,
      newCard: next.cardCounter > cardBefore,
      activeSegId: next.activeSegId,
      cards: next.cardOrder.length,
      createdAt: event.createdAt
    });
    if (next.cardCounter > cardBefore && (event.isFinal || event.isTranslationUpdate) && event.kind === "transcript") {
      streamLog("reduce", "WARN new card created by final/translation event", {
        party: event.party,
        segmentId: event.segmentId,
        isLive: event.isLive,
        isFinal: event.isFinal
      });
    }
  }
  return next;
}

export function reduceMany(state: StreamState, events: NormalizedEvent[]): StreamState {
  return events.reduce((acc, ev) => reduce(acc, ev), state);
}

export function hydrateHistory(events: NormalizedEvent[], callId?: string): StreamState {
  let state = createInitialState(callId);
  const ordered = events
    .map((e, i) => ({ e, i }))
    .sort((a, b) => {
      const ao = a.e.historyOrder ?? Number.MAX_SAFE_INTEGER;
      const bo = b.e.historyOrder ?? Number.MAX_SAFE_INTEGER;
      return ao - bo || a.i - b.i;
    })
    .map((x) => x.e);
  ordered.forEach((e, idx) => {
    state = cloneState(state);
    const seg = createSegment(state, e.party, e, { orderSeq: idx + 1, historical: true });
    const t = touchSeg(state, seg.segId);
    if (e.original != null) t.rolling = e.original;
    if (e.english) {
      t.interp = e.english;
      t.englishSource = "mt";
    } else if (isEnglishLang(e.sourceLang) && e.original) {
      t.interp = e.original;
      t.englishSource = "mt";
    }
    t.sourceLang = e.sourceLang || "";
    t.targetLang = e.targetLang || "";
    if (e.aiInterpreter) t.aiInterpreter = true;
    t.hasRolling = true;
    t.rollingFinal = true;
    t.interpFinal = true;
    t.rollingRevision = e.revision ?? 1;
    registerKeys(state, bindKeys(e), seg.segId);
    propagateSegLang(state, t);
  });
  recompute(state);
  return state;
}

/** Force-finalize every open segment (call end). */
export function finalizeAll(state: StreamState): StreamState {
  const next = cloneState(state);
  for (const segId of Object.keys(next.segments)) {
    const seg = next.segments[segId];
    if (!seg.finalized) {
      const s = touchSeg(next, segId);
      if (s.hasRolling) s.rollingFinal = true;
      if (s.interpStarted) s.interpFinal = true;
      s.finalized = true;
      s.status = "finalized";
    }
  }
  next.pendingTypeToTalkSeg = null;
  next.activeSegId = null;
  recompute(next);
  next.activeSegId = null;
  return next;
}
